<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :scrollable="true"
    icon="fa-desktop"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="stationForm" :labelColumnSize="3">
      <FormRow label="Name">
        <FormInputText v-model="stationData.name" field="name" />
      </FormRow>
      <FormRow label="Type">
        <FormSelect v-model="stationData.type" field="type" :options="stationCategoryOptions" />
      </FormRow>
      <FormRow label="Location">
        <AccountLocationSelect v-model="stationData.accountLocationId" field="accountLocationId" />
      </FormRow>
      <FormRow label="Scanning Types">
        <FormCheckbox label="Scan In" v-model="stationData.isScanInAvailable" field="isScanInAvailable" />&nbsp;&nbsp;&nbsp;
        <FormCheckbox label="Scan Out" v-model="stationData.isScanOutAvailable" field="isScanOutAvailable" />
      </FormRow>
      <FormRow label="UOM Type">
        <FormSelect v-model="stationData.uomType" field="uomType" :options="UOMTypeLookup" note="Unit of Measure used for cost / performance analytics" />
      </FormRow>
      <FormRow label="Show Station Graph">
        <FormCheckbox v-model="stationData.showStationGraph" field="showStationGraph" />
      </FormRow>
      <FormRow label="Queue Display Type" helpTip="For stations with queues active, displays how to show panels scanned into the station queue">
        <FormSelect v-model="stationData.queueDisplayType" field="queueDisplayType" :options="queueDisplayTypeLookup" />
      </FormRow>


      <FormHorizontalRule />
      <FormRow label="Secondary Station">
        <FormSelect v-model="stationData.secondaryStationId" field="secondaryStationId" :optionsPromise="productionStationOptionsPromise" />
      </FormRow>
      <FormRow label="Production Stages">
        <FormMultiSelect v-model="stationData.productionStages" field="productionStages" :options="productionStageList" :loading="productionStageLoading" placeholder="Select Production Stages" note="A production station can be used by one or more stages" />
      </FormRow>
      <FormHorizontalRule />
      <strong>Station Capabilities</strong>

      <FormRow label="Glass Types">
        <FormSelect v-model="stationData.glassType" field="glassType" :options="GlassTypeLookup" />
      </FormRow>
      <FormRow label="Minimum Panel Size">
        <div class="row no-gutter">
          <div class="col-6">
            <FormInputText type="number" v-model="stationData.sizeMinHeight" field="sizeMinHeight" prepend="Height" addon="mm" />
          </div>
          <div class="col-6">
            <FormInputText type="number" v-model="stationData.sizeMinWidth" field="sizeMinWidth" prepend="Width" addon="mm" />
          </div>
        </div>
      </FormRow>
      <FormRow label="Maximum Panel Size">
        <div class="row no-gutter">
          <div class="col-6">
            <FormInputText type="number" v-model="stationData.sizeMaxHeight" field="sizeMaxHeight" prepend="Height" addon="mm" />
          </div>
          <div class="col-6">
            <FormInputText type="number" v-model="stationData.sizeMaxWidth" field="sizeMaxWidth" prepend="Width" addon="mm" />
          </div>
        </div>
      </FormRow>
      <FormRow label="Thickness Range">
        <div class="row no-gutter">
          <div class="col-6">
            <FormInputText type="number" v-model="stationData.minThickness" field="minThickness" prepend="Min" addon="mm" />
          </div>
          <div class="col-6">
            <FormInputText type="number" v-model="stationData.maxThickness" field="maxThickness" prepend="Max" addon="mm" />
          </div>
        </div>
      </FormRow>
      <FormRow label="Station Capacity">
        <FormInputText type="number" v-model="stationData.capacityUnitsPerHour" field="capacityUnitsPerHour" :addon="`${formatLookupName(stationData.uomType, UOMTypeUnitsLookup)} / Hour`" />
      </FormRow>
      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import { UOMTypeLookup, UOMTypeUnitsLookup, GlassTypes, GlassTypeLookup, ProductionStationTypes, ProductionStationTypeLookup, ProductionStationQueueDisplayTypes, ProductionStationQueueDisplayTypeLookup } from '@/store/types'

export default {
  name: 'ProductionStationDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    AccountLocationSelect
  },
  props: {
    productionStageId: Number,
    productionStationId: Number,
    isCreateMode: Boolean
  },
  data() {
    return {
      stationData: {
      },
      stationForm: {},
      submitting: false,
      productionStageLoading: true,
      productionStageList: [],
      productionStationOptionsPromise: this.getProductionStationOptionsPromise(),
    }
  },
  created() {
    this.UOMTypeLookup = UOMTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
    this.GlassTypeLookup = GlassTypeLookup
    this.ProductionStationTypes = ProductionStationTypes
  },
  mounted() {
    // Load list of production stages
    this.productionStageLoading = true
    this.$apiService.productionService.getProductionStageList()
    .then(response => {
      this.productionStageList = response.data.map(stage => {
        return {
          value: stage.id,
          name: stage.name,
          colour: stage.highlightColour
        }
      })
    })
    .finally(() => {
      this.productionStageLoading = false
    })
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Production Station' : 'Edit Production Station'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    stationCategoryOptions() {
      var optionList = _.clone(ProductionStationTypeLookup)

      // Filter out types if the account does not have access to it
      if(!this.accountHasProductFeature(this.AccountProductFeatureTypes.productionFurnace))
        optionList = optionList.filter(stationType => stationType.value != ProductionStationTypes.furnace && stationType.value != ProductionStationTypes.furnaceUnload)
      if(!this.accountHasProductFeature(this.AccountProductFeatureTypes.productionOptimisation))
        optionList = optionList.filter(stationType => stationType.value != ProductionStationTypes.cuttingOptimisation && stationType.value != ProductionStationTypes.cuttingBreakout)
      if(!this.accountHasProductFeature(this.AccountProductFeatureTypes.orderEditor))
        optionList = optionList.filter(stationType => stationType.value != ProductionStationTypes.waterjet)

      return optionList
    },
    queueDisplayTypeLookup() {
      return ProductionStationQueueDisplayTypeLookup
    }
  },
  methods: {
    show(productionStation) {
      // Reset form
      this.stationData = {
        name: '',
        type: '',
        isScanInAvailable: true,
        isScanOutAvailable: false,
        showStationGraph: true,
        glassType: GlassTypes.all,
        productionStages: [],
        secondaryStationId: null,
        queueDisplayType: ProductionStationQueueDisplayTypes.smallThumbnail,
        accountLocationId: null
      }
      this.stationForm = {}
      this.submitting = false

      if(productionStation != null) {
        this.stationData = _.cloneDeep(productionStation)
        this.stationData.productionStages = []
        if(_.isArray(productionStation.productionStages))
          this.stationData.productionStages = productionStation.productionStages.map(stage => stage.id)
      } else {
        if(this.$props.productionStageId)
          this.stationData.productionStages.push(this.$props.productionStageId)
      }

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true

      var stationData = _.cloneDeep(this.$data.stationData)

      if(this.isCreateMode) {
        this.$apiService.productionService.createProductionStation(stationData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.stationForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.productionService.updateProductionStation(stationData.id, stationData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.stationForm = this.processFormResponse(err)
        })
      }
    },
    getProductionStationOptionsPromise() {
      return new Promise((resolve, reject) => {
        return this.$apiService.productionService.getProductionStationList()
        .then(response => {
          var staionOptions = response.data
          .filter(station => {
            return station.id != this.$data.stationData.id
          })
          .map(station => {
            return { value: station.id, name: station.name }
          })
          resolve(_.concat({ value: null, name: 'None' }, staionOptions))
        })
        .catch(err => { reject(err) })
      })
    }
  }
}
</script>